import { captureException } from '@sentry/browser'

import {
    changeCurrencyCode as changeCurrencyCodeAction,
    createBrandGroupSuccess,
    deleteBrandGroupSuccess,
    dismissUserSurveySuccess,
    fetchAutomationCapabilitiesSuccess,
    fetchBrandGroupsSuccess,
    fetchCustomEventsSuccess,
    fetchOrganizationLabelsSuccess,
    fetchSovPermissionsSuccess,
    fetchTriggeredAlertsForDrawerFailure,
    fetchTriggeredAlertsForDrawerRequest,
    fetchTriggeredAlertsForDrawerSuccess,
    setHiddenAutomationCapabilities as setHiddenAutomationCapabilitiesAction,
    setHomeDashboardId as setHomeDashboardIdAction,
    updateBrandGroupSuccess,
} from 'actions/ui/app'
import { getTriggeredAlerts } from 'api/Cerebro/orgScoped/alertsApi'
import { getAutomationCapabilities } from 'api/Cerebro/orgScoped/automationsApi'
import {
    createBrandGroup as createBrandGroupApi,
    deleteBrandGroup as deleteBrandGroupApi,
    getBrandGroups,
    updateBrandGroup as updateBrandGroupApi,
} from 'api/Cerebro/orgScoped/brandGroupsApi'
import { getCustomEvents } from 'api/Cerebro/orgScoped/customEventsApi'
import { getLabels } from 'api/Cerebro/orgScoped/labelsApi'
import { getSovPermissions } from 'api/Cerebro/orgScoped/userApi'
import {
    cerebroApiRequest,
    cerebroApiRequestGuarded,
} from 'api/cerebroApiRequest'
import { updateUserSettings } from 'features/Auth/service/userSettings'
import { getCurrentPage } from 'routing/pagePaths'
import { PageId } from 'routing/pages'
import { selectOrgKey } from 'selectors/auth'
import numeral from 'utilities/numeral'

import { dispatch, getReduxState } from './reduxBridge'

const TRIGGERED_ALERTS_PARAMS = {
    ordering: '-triggered_at',
    limit: 15,
    am_subscribed: true,
    subscription_type: 'in-app',
}

type ShouldCommit = () => boolean

/**
 * Route through the context-guarded request when the bootstrap/org-switch flow
 * supplies a `shouldCommit` guard, and through the plain request otherwise, so
 * standalone callers keep the exact behavior they had before.
 */
const request = (
    shouldCommit: ShouldCommit | undefined,
    ...args: Parameters<typeof cerebroApiRequest>
): Promise<any> =>
    shouldCommit
        ? cerebroApiRequestGuarded(shouldCommit, ...args)
        : cerebroApiRequest(...args)

export async function fetchOrganizationLabels(
    shouldCommit?: ShouldCommit
): Promise<void> {
    await request(shouldCommit, fetchOrganizationLabelsSuccess, getLabels, {
        limit: 'null', // fetch all available labels when loading app
    })
}

export async function fetchAutomationCapabilities(
    shouldCommit?: ShouldCommit
): Promise<void> {
    await request(
        shouldCommit,
        fetchAutomationCapabilitiesSuccess,
        getAutomationCapabilities
    )
}

export async function fetchCustomEvents(
    shouldCommit?: ShouldCommit
): Promise<void> {
    await request(
        shouldCommit,
        fetchCustomEventsSuccess,
        getCustomEvents,
        null,
        {
            headers: { noCount: true },
        }
    )
}

export async function fetchBrandGroups(
    shouldCommit?: ShouldCommit
): Promise<void> {
    await request(shouldCommit, fetchBrandGroupsSuccess, getBrandGroups, null, {
        headers: { noCount: true },
    })
}

export async function fetchSovPermissions(
    shouldCommit?: ShouldCommit
): Promise<void> {
    await request(shouldCommit, fetchSovPermissionsSuccess, getSovPermissions)
}

export async function fetchTriggeredAlerts(
    shouldCommit?: ShouldCommit
): Promise<void> {
    try {
        // The api client resolves on 4xx (validateStatus < 500), so gate on
        // the status — an error body has no `results` and would poison the
        // triggeredAlerts state. A non-2xx clears the list instead, so an org
        // switch that loses access can't leave the previous org's alerts in
        // the nav badge.
        const { status, data } = await getTriggeredAlerts(
            TRIGGERED_ALERTS_PARAMS
        )
        if (!shouldCommit || shouldCommit()) {
            dispatch(
                fetchTriggeredAlertsForDrawerSuccess(
                    status >= 200 && status < 300 ? data : { results: [] }
                )
            )
        }
    } catch (error) {
        captureException(error)
    }
}

export async function fetchTriggeredAlertsForDrawer(): Promise<void> {
    dispatch(fetchTriggeredAlertsForDrawerRequest())
    try {
        await cerebroApiRequest(
            fetchTriggeredAlertsForDrawerSuccess,
            getTriggeredAlerts,
            TRIGGERED_ALERTS_PARAMS
        )
    } catch (error) {
        dispatch(fetchTriggeredAlertsForDrawerFailure(error))
    }
}

interface BrandGroupData {
    [key: string]: any
}

export async function createBrandGroup(
    data: BrandGroupData,
    callback?: (brandGroup?: any) => void
): Promise<void> {
    const response = await cerebroApiRequest(
        createBrandGroupSuccess,
        createBrandGroupApi,
        data
    )
    callback?.(response.data)
}

export async function updateBrandGroup(
    brandGroupId: string,
    data: BrandGroupData,
    callback?: (brandGroup?: any) => void
): Promise<void> {
    const response = await cerebroApiRequest(
        updateBrandGroupSuccess,
        updateBrandGroupApi,
        brandGroupId,
        data
    )
    callback?.(response.data)
}

export async function deleteBrandGroup(
    brandGroupId: string,
    callback?: () => void
): Promise<void> {
    await cerebroApiRequest(null, deleteBrandGroupApi, brandGroupId)
    dispatch(deleteBrandGroupSuccess(brandGroupId))
    callback?.()
}

/**
 * Set the numeral locale for the displayed currency. Skipped on pages that own
 * their own currency formatting (dashboard/home), matching the saga.
 */
export function setDisplayCurrency(currencyCode: string): void {
    const { page: currentPageName } = getCurrentPage()
    const pagesToCheck: PageId[] = ['dashboardPage', 'homePage']
    if (!currentPageName || !pagesToCheck.includes(currentPageName)) {
        numeral.locale(currencyCode)
    }
}

export async function changeCurrencyCode(currencyCode: string): Promise<void> {
    dispatch(changeCurrencyCodeAction({ currencyCode }))

    const orgKey = selectOrgKey(getReduxState())
    setDisplayCurrency(currencyCode)
    await updateUserSettings([
        { path: ['currency', orgKey], value: currencyCode },
    ])
}

export async function setHomeDashboardId(
    dashboardId: string | null
): Promise<void> {
    dispatch(setHomeDashboardIdAction({ dashboardId }))

    const orgKey = selectOrgKey(getReduxState())
    await updateUserSettings([
        { path: ['homeDashboardId', orgKey], value: dashboardId },
    ])
}

export async function setHiddenAutomationCapabilities(
    automationCapabilities: string[]
): Promise<void> {
    dispatch(setHiddenAutomationCapabilitiesAction(automationCapabilities))
    await updateUserSettings([
        {
            path: ['hiddenAutomationCapabilities'],
            value: automationCapabilities,
        },
    ])
}

export async function dismissUserSurvey(lastReadDate: string): Promise<void> {
    try {
        dispatch(dismissUserSurveySuccess(lastReadDate))
        await updateUserSettings([
            { path: ['feedbackSurveyLastRead'], value: lastReadDate },
        ])
    } catch (error) {
        captureException(error)
    }
}
